/**
 * §P.12 · Las reservas de clase de prueba.
 *
 * ## La pantalla se ordena por el reloj, no por la fecha
 *
 * `hours_waiting` viene en cada fila porque **es la única métrica que decide si esto
 * funciona**: una reserva contestada en dos horas se convierte; una contestada en
 * tres días es una familia que ya está apuntada en otro sitio. El servidor devuelve
 * las que llevan más esperando primero y aquí no se reordena.
 *
 * Y por eso el reloj se pinta en grande y cambia de color: es el dato que tiene que
 * hacer que alguien coja el teléfono ahora, no el nombre ni el grupo.
 *
 * ## Lo vivo por defecto
 *
 * Sin `include_closed` la lista trae lo que todavía pide algo de alguien. Las que se
 * apuntaron y las que no siguieron están detrás de una pestaña, no delante: una
 * bandeja que empieza con veinte filas cerradas es una bandeja que se deja de mirar.
 *
 * ## «Se apuntó» pide decir a quién, y está bien que lo pida
 *
 * `422 converted_student_required`. El contrato lo explica: «sin ese enlace, la cifra
 * de conversión del embudo de §P.9 diría que el club convierte y no habría forma de
 * comprobarlo». Así que el botón no manda el cambio a ciegas: abre un buscador de
 * alumnos y espera. Si el alumno todavía no existe, se le da de alta primero — y la
 * pantalla lo dice, porque si no parece que falta un botón.
 *
 * ## «No siguió» no se esconde
 *
 * Es el otro final y no es un fracaso que tapar: junto al reloj es lo que dice si se
 * pierden por precio o por tardar tres días en llamar. Va al lado de los demás y sin
 * confirmación: no destruye nada y siempre se puede volver a mover.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { cuenta, fecha, fechaParaInput } from '@/shared/ui/formato';
import type { Alumno, PaginaDeAlumnos } from '@/club/alumnos/tipos';
import {
  ABIERTAS,
  espera,
  ESTADOS_DE_RESERVA,
  ORIGENES,
  type ReservaDePrueba,
} from '@/club/directorio/tipos';

const DIAS = [
  'lunes',
  'martes',
  'miércoles',
  'jueves',
  'viernes',
  'sábado',
  'domingo',
];

export function ClasesDePrueba({ club }: { club: string }) {
  const cache = useQueryClient();
  const [verCerradas, setVerCerradas] = useState(false);
  const [problema, setProblema] = useState<Problema | null>(null);
  /** La reserva cuya conversión se está resolviendo, y lo tecleado en el buscador. */
  const [apuntando, setApuntando] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  /** La reserva a la que se le está poniendo día. */
  const [citando, setCitando] = useState<string | null>(null);

  const reservas = useQuery({
    queryKey: ['reservas', club, verCerradas],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['trial-bookings'].get({
          query: {
            limit: 200,
            ...(verCerradas ? { include_closed: true } : {}),
          },
        });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as ReservaDePrueba[];
    },
  });

  /*
   * Los alumnos solo se piden cuando hace falta enlazar uno: es una lista de
   * doscientos y no tiene por qué viajar para mirar la bandeja.
   */
  const alumnos = useQuery({
    queryKey: ['alumnos-para-enlazar', club, busqueda],
    enabled: apuntando !== null,
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).students.get({
        query: {
          limit: 10,
          status: 'active',
          ...(busqueda ? { q: busqueda } : {}),
        },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PaginaDeAlumnos;
    },
  });

  const cambiar = useMutation({
    mutationFn: async ({
      id,
      cambios,
    }: {
      id: string;
      cambios: Record<string, unknown>;
    }) => {
      const r = await api.v1
        .clubs({ club })
        ['trial-bookings']({ id })
        .patch(cambios);
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as ReservaDePrueba;
    },
    onSuccess: () => {
      setApuntando(null);
      setCitando(null);
      setBusqueda('');
      setProblema(null);
      void cache.invalidateQueries({ queryKey: ['reservas', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (reservas.isPending) {
    return <Cargando filas={4} etiqueta="Cargando las reservas" />;
  }

  if (reservas.isError || !reservas.data) {
    return (
      <PanelError
        problema={aProblema(reservas.error)}
        reintentar={() => void reservas.refetch()}
      />
    );
  }

  const lista = reservas.data;
  const sinContestar = lista.filter((r) => r.status === 'new').length;

  return (
    <section>
      <header className="titulo-fila">
        <h1>Clases de prueba</h1>
        <nav className="pestañas" aria-label="Qué reservas se enseñan">
          <button
            type="button"
            className={verCerradas ? 'btn-plano' : 'pestaña-activa'}
            onClick={() => setVerCerradas(false)}
          >
            Abiertas
          </button>
          <button
            type="button"
            className={verCerradas ? 'pestaña-activa' : 'btn-plano'}
            onClick={() => setVerCerradas(true)}
          >
            Todas
          </button>
        </nav>
      </header>

      <p className="tenue">
        Quien te ha encontrado en el buscador y quiere venir a probar.{' '}
        <strong>Contestar el mismo día es lo que las convierte</strong>: a los
        tres días la familia ya está apuntada en otro sitio.
      </p>

      {problema && <PanelError problema={problema} />}

      {sinContestar > 0 && (
        <p className="nota">
          <strong>
            {cuenta(sinContestar, 'reserva', 'reservas')} sin contestar.
          </strong>{' '}
          Es la lista que hay que dejar vacía cada día.
        </p>
      )}

      {lista.length === 0 ? (
        <Vacio
          titulo={
            verCerradas
              ? 'Todavía no ha llegado ninguna reserva'
              : 'Nada pendiente'
          }
          explicacion={
            verCerradas
              ? 'Llegan de tu ficha del buscador. Si no está publicada, nadie puede reservar: se publica en «El club» → «Ficha del buscador».'
              : 'No hay ninguna reserva esperando respuesta. Las cerradas están en «Todas».'
          }
        />
      ) : (
        <ul className="papeles">
          {lista.map((r) => {
            const reloj = espera(r.hours_waiting);
            const abierta = ABIERTAS.includes(r.status);

            return (
              <li key={r.id}>
                <div className="aviso-cabeza">
                  <strong>{r.contact_name}</strong>
                  {reloj && (
                    <span className={`pastilla ${reloj.clase} pastilla-left`}>
                      {reloj.texto}
                    </span>
                  )}
                  <span className="pastilla pastilla-left">
                    {ESTADOS_DE_RESERVA[r.status]}
                  </span>
                  <span className="tenue">{ORIGENES[r.source]}</span>
                </div>

                <p>
                  {/*
                    El teléfono y el correo son enlaces de verdad: esta pantalla se
                    abre para llamar, y copiar un número a mano desde la pantalla es
                    donde se pierde el minuto que decide la conversión.
                  */}
                  {r.phone && (
                    <>
                      <a href={`tel:${r.phone.replace(/\s/g, '')}`}>
                        {r.phone}
                      </a>{' '}
                    </>
                  )}
                  {r.email && <a href={`mailto:${r.email}`}>{r.email}</a>}
                </p>

                <p className="tenue">
                  {r.student_name ?? 'Sin decir quién viene'}
                  {r.student_age !== null && ` · ${r.student_age} años`}
                  {r.preferred_days.length > 0 &&
                    ` · le vienen bien ${r.preferred_days
                      .map((d) => DIAS[d - 1])
                      .join(', ')}`}
                  {/*
                    `preferred_time` es texto libre: la familia escribe «18:30» o
                    escribe «por la tarde». Anteponer «a las» a lo segundo daba «a
                    las por la tarde». Se antepone solo cuando parece una hora.
                  */}
                  {r.preferred_time &&
                    (/^\d{1,2}[:.]\d{2}/.test(r.preferred_time)
                      ? ` a las ${r.preferred_time}`
                      : ` · ${r.preferred_time}`)}
                  {r.group_name && ` · ${r.group_name}`}
                  {' · llegó el '}
                  {fecha(r.created_at)}
                </p>

                {r.message && <p>«{r.message}»</p>}

                {r.scheduled_for && (
                  <p className="nota nota-bien">
                    Citada para el {fecha(r.scheduled_for)}.
                  </p>
                )}

                {/* ── Poner día ─────────────────────────────────────────── */}
                {citando === r.id && (
                  <div className="hueco hueco-confirmar">
                    <label>
                      ¿Qué día viene?
                      <input
                        type="date"
                        defaultValue={fechaParaInput(r.scheduled_for)}
                        onChange={(e) => {
                          if (e.target.value === '') return;
                          cambiar.mutate({
                            id: r.id,
                            cambios: {
                              status: 'scheduled',
                              scheduled_for: new Date(
                                `${e.target.value}T18:00:00`
                              ).toISOString(),
                            },
                          });
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn-plano"
                      onClick={() => setCitando(null)}
                    >
                      Dejarlo
                    </button>
                  </div>
                )}

                {/* ── Enlazar con el alumno que se ha dado de alta ───────── */}
                {apuntando === r.id && (
                  <div className="hueco hueco-confirmar">
                    <p>
                      <strong>¿Quién es, de tus alumnos?</strong> Hace falta
                      decirlo: es lo que permite comprobar después cuántas
                      reservas se convirtieron de verdad.
                    </p>

                    <label>
                      Buscar
                      <input
                        autoFocus
                        placeholder={r.student_name ?? 'Nombre del alumno'}
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                      />
                    </label>

                    {alumnos.isPending ? (
                      <p className="tenue">Buscando…</p>
                    ) : alumnos.data && alumnos.data.data.length > 0 ? (
                      <ul className="candidatos">
                        {alumnos.data.data.map((a: Alumno) => (
                          <li key={a.id}>
                            <button
                              type="button"
                              className="enlace-fila"
                              disabled={cambiar.isPending}
                              onClick={() =>
                                cambiar.mutate({
                                  id: r.id,
                                  cambios: {
                                    status: 'enrolled',
                                    converted_student_id: a.id,
                                  },
                                })
                              }
                            >
                              {a.first_name} {a.last_name ?? ''}
                              {a.age !== null && (
                                <span className="tenue"> · {a.age} años</span>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="tenue">
                        Ningún alumno con ese nombre.{' '}
                        <strong>Si todavía no lo has dado de alta</strong>,
                        hazlo primero en «Alumnos» y vuelve aquí a enlazarlo.
                      </p>
                    )}

                    <button
                      type="button"
                      className="btn-plano"
                      onClick={() => {
                        setApuntando(null);
                        setBusqueda('');
                      }}
                    >
                      Dejarlo
                    </button>
                  </div>
                )}

                {/* ── Lo que se puede hacer ─────────────────────────────── */}
                {abierta && citando !== r.id && apuntando !== r.id && (
                  <div className="acciones">
                    {r.status === 'new' && (
                      <button
                        type="button"
                        className="btn-plano"
                        disabled={cambiar.isPending}
                        onClick={() =>
                          cambiar.mutate({
                            id: r.id,
                            cambios: { status: 'contacted' },
                          })
                        }
                      >
                        Ya la he llamado
                      </button>
                    )}

                    <button
                      type="button"
                      className="btn-plano"
                      onClick={() => setCitando(r.id)}
                    >
                      {r.scheduled_for ? 'Cambiar el día' : 'Ponerle día'}
                    </button>

                    {r.status !== 'attended' && (
                      <button
                        type="button"
                        className="btn-plano"
                        disabled={cambiar.isPending}
                        onClick={() =>
                          cambiar.mutate({
                            id: r.id,
                            cambios: { status: 'attended' },
                          })
                        }
                      >
                        Vino a probar
                      </button>
                    )}

                    <button
                      type="button"
                      className="btn-primario"
                      onClick={() => setApuntando(r.id)}
                    >
                      Se apuntó
                    </button>

                    <button
                      type="button"
                      className="btn-plano"
                      disabled={cambiar.isPending}
                      onClick={() =>
                        cambiar.mutate({
                          id: r.id,
                          cambios: { status: 'lost' },
                        })
                      }
                    >
                      No siguió
                    </button>
                  </div>
                )}

                {r.status === 'enrolled' && (
                  <p className="tenue">
                    Se apuntó
                    {r.scheduled_for &&
                      ` tras probar el ${fecha(r.scheduled_for)}`}
                    . Es la conversión que hace que el directorio valga la pena.
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
